import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  useActionReminderPolicy,
  useUpdateActionReminderPolicy,
} from '../hooks/useActionReminderPolicy';
import { useActionAssigneeDirectory } from '../hooks/useActionAssigneeDirectory';
import { ORGANIZATION } from '../ICSConstants';
import {
  describeReminderSchedule,
  draftToPolicyBody,
  policyToDraft,
  type ReminderPolicyDraft,
} from './reminderPolicyModel';

const NONE_ESCALATION = '__none__';

type Props = {
  show: boolean;
  onClose: () => void;
};

/**
 * The organization's reminder and SLA rules. The backend already schedules
 * nudges and breach escalation; this is the form that was missing.
 */
export const ActionReminderPolicyEditor: React.FC<Props> = ({
  show,
  onClose,
}) => {
  const { data: policy, isLoading } = useActionReminderPolicy(show);
  const update = useUpdateActionReminderPolicy();
  const directory = useActionAssigneeDirectory(ORGANIZATION);
  const [override, setOverride] = useState<ReminderPolicyDraft | null>(null);
  const draft = override ?? (policy ? policyToDraft(policy) : null);

  const preview = useMemo(
    () => (draft ? draftToPolicyBody(draft) : null),
    [draft]
  );

  const updateDraft = (
    fn: (current: ReminderPolicyDraft) => ReminderPolicyDraft
  ) => {
    setOverride((current) => {
      const base = current ?? (policy ? policyToDraft(policy) : null);
      return base ? fn(base) : current;
    });
  };

  const close = () => {
    setOverride(null);
    onClose();
  };

  const save = () => {
    if (!draft) return;
    update.mutate(draftToPolicyBody(draft), {
      onSuccess: () => {
        setOverride(null);
        onClose();
      },
    });
  };

  const selectedEscalation = directory.users.find(
    (user) => user.value === draft?.breachEscalateTo
  );

  return (
    <Dialog
      open={show}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="dark">
        <DialogHeader>
          <DialogTitle>Reminder and SLA policy</DialogTitle>
        </DialogHeader>
        {isLoading || !draft ? (
          <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
            Loading policy…
          </p>
        ) : (
          <>
            <label className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
              <Checkbox
                checked={draft.enabled}
                onCheckedChange={(checked) =>
                  updateDraft((current) => ({
                    ...current,
                    enabled: checked === true,
                  }))
                }
              />
              Send reminders for open tasks
            </label>
            <Label htmlFor="remind-offsets">
              Remind this many minutes before the due date
            </Label>
            <Textarea
              id="remind-offsets"
              rows={4}
              value={draft.remindBeforeText}
              onChange={(event) =>
                updateDraft((current) => ({
                  ...current,
                  remindBeforeText: event.target.value,
                }))
              }
            />
            <p className="tw:text-xs tw:text-muted-foreground">
              One number per line, furthest first after save. At most four, none
              after 30 days.{' '}
              {preview
                ? describeReminderSchedule(preview.remindBeforeMinutes)
                : ''}
            </p>
            <Label htmlFor="breach-grace">
              Escalate this many minutes after the due date
            </Label>
            <Input
              id="breach-grace"
              type="number"
              min={0}
              value={draft.breachEscalateAfterMinutes}
              onChange={(event) =>
                updateDraft((current) => ({
                  ...current,
                  breachEscalateAfterMinutes: event.target.value,
                }))
              }
            />
            <Label htmlFor="breach-to">Escalate to</Label>
            <Select
              value={draft.breachEscalateTo || NONE_ESCALATION}
              onValueChange={(next) =>
                updateDraft((current) => ({
                  ...current,
                  breachEscalateTo:
                    !next || next === NONE_ESCALATION ? '' : String(next),
                }))
              }
            >
              <SelectTrigger id="breach-to" className="tw:w-full">
                <SelectValue placeholder="Nobody (assignee only)">
                  {selectedEscalation?.label ?? 'Nobody (assignee only)'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value={NONE_ESCALATION}>
                  Nobody (assignee only)
                </SelectItem>
                {directory.users.map((user) => (
                  <SelectItem key={user.value} value={user.value}>
                    {user.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!draft || update.isPending || isLoading}
            onClick={save}
          >
            Save policy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ActionReminderPolicyButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
      >
        Reminder policy
      </Button>
      <ActionReminderPolicyEditor show={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default ActionReminderPolicyEditor;
